/**
 * Each approval card's decision, shared by approval ID across chat runtimes.
 * Leaving a thread while a decision is in flight and opening it again builds a
 * new runtime from cached history, which can still hold the card as pending:
 * its banner reads the decision from here, so it shows the request running or
 * done instead of offering the card a second time.
 */
import { useSyncExternalStore } from 'react';

export type ApprovalDecision = {
  /** The request in flight, if any. */
  loading: 'approve' | 'reject' | null;
  resolved: 'approved' | 'rejected' | null;
  error: string | null;
};

const IDLE: ApprovalDecision = { loading: null, resolved: null, error: null };
const decisions = new Map<string, ApprovalDecision>();
const listeners = new Set<() => void>();

function notify() {
  for (const listener of listeners) listener();
}

export function readApprovalDecision(approvalId: string): ApprovalDecision {
  return decisions.get(approvalId) ?? IDLE;
}

export function updateApprovalDecision(approvalId: string, patch: Partial<ApprovalDecision>) {
  decisions.set(approvalId, { ...readApprovalDecision(approvalId), ...patch });
  notify();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function useApprovalDecision(approvalId: string): ApprovalDecision {
  return useSyncExternalStore(
    subscribe,
    () => readApprovalDecision(approvalId),
    () => IDLE,
  );
}
